import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
  subscribeToAccessToken,
} from "utils/authToken";
import {
  fetchCurrentUser,
  loginRequest,
  type AuthenticatedUser,
} from "utils/authApi";

type LoginCredentials = {
  username: string;
  password: string;
};

type AuthContextValue = {
  isAuthenticated: boolean;
  user: AuthenticatedUser | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  loading: boolean;
  initializing: boolean;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [hasToken, setHasToken] = useState(() => Boolean(getAccessToken()));
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(Boolean(getAccessToken()));

  const refreshUser = useCallback(async () => {
    if (!hasToken) {
      setUser(null);
      return;
    }
    try {
      const profile = await fetchCurrentUser();
      setUser(profile);
    } catch (error) {
      clearAccessToken();
      setUser(null);
      throw error;
    }
  }, [hasToken]);

  useEffect(() => {
    const unsubscribe = subscribeToAccessToken((token) => {
      setHasToken(Boolean(token));
      if (!token) {
        setUser(null);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    let active = true;

    if (!hasToken) {
      setInitializing(false);
      setUser(null);
      return () => {
        active = false;
      };
    }

    const loadProfile = async () => {
      setInitializing(true);
      try {
        const profile = await fetchCurrentUser();
        if (active) {
          setUser(profile);
        }
      } catch {
        clearAccessToken();
        if (active) {
          setUser(null);
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    loadProfile();

    return () => {
      active = false;
    };
  }, [hasToken]);

  const login = async ({ username, password }: LoginCredentials) => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername || !password.trim()) {
      throw new Error("Username and password are required.");
    }

    setLoading(true);
    try {
      const { accessToken } = await loginRequest(trimmedUsername, password);
      setAccessToken(accessToken);
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    clearAccessToken();
    setUser(null);
    navigate("/", { replace: true });
  }, [navigate]);

  const isAuthenticated = hasToken && !!user;

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      login,
      logout,
      loading,
      initializing,
      refreshUser,
    }),
    [isAuthenticated, user, loading, initializing, refreshUser, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
